import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, Sparkles, Copy, Download, FileText, Globe } from 'lucide-react';
import { toast } from 'sonner';
import PortalLayout from './PortalLayout';
import { Markdown } from '../../components/coaching/JumpstartStudio';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const muted = { color: 'var(--p-muted)' };

export default function ClientBlueprint() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/portal/coaching`, { withCredentials: true })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const copy = (md) => { navigator.clipboard.writeText(md); toast.success('Blueprint copied'); };
  const download = (it) => {
    const md = it.deliverable?.markdown || '';
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${(it.business_name || 'my').replace(/\s+/g, '-')}-jumpstart-blueprint.md`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <PortalLayout active="blueprint">
      <div className="mb-8" data-testid="client-blueprint-page">
        <span className="text-xs tracking-[0.2em] uppercase text-[#F97316] font-semibold p-eyebrow">My Blueprint</span>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-light mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="blueprint-title">
          Your Business Jumpstart Blueprint
        </h1>
        <p className="mt-2 max-w-2xl" style={muted}>The strategic foundation we built together — mission, positioning, offers, and your launch plan. Yours to keep.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#1D4ED8]" size={28} /></div>
      ) : items.length === 0 ? (
        <div className="p-card rounded-2xl p-10 text-center" data-testid="no-blueprint">
          <FileText size={36} className="mx-auto mb-3" style={{ color: 'var(--p-faint)' }} />
          <p className="text-sm" style={muted}>Your blueprint will appear here after your Business Jumpstart Session.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {items.map((it) => (
            <div key={it.session_id} className="p-card rounded-2xl p-6" data-testid={`blueprint-${it.session_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#1D4ED8]">
                  <Sparkles size={14} /> {it.business_name || 'Your Business'}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => copy(it.deliverable?.markdown || '')} data-testid="client-copy-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg border border-[#E2E8F0] hover:border-[#1D4ED8]"><Copy size={14} /> Copy</button>
                  <button onClick={() => download(it)} data-testid="client-download-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg border border-[#E2E8F0] hover:border-[#1D4ED8]"><Download size={14} /> Download</button>
                  {it.website_slug && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/coaching/site/${it.website_slug}`} target="_blank" rel="noopener noreferrer" data-testid="client-website-btn"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg bg-[#F97316] text-white hover:bg-[#EA580C]"><Globe size={14} /> My Website</a>
                  )}
                </div>
              </div>
              <Markdown text={it.deliverable?.markdown || ''} />
            </div>
          ))}
        </div>
      )}
    </PortalLayout>
  );
}
